import React, { ReactElement, useRef, useEffect, Dispatch, useState, PropsWithChildren, MouseEventHandler } from 'react';
import { Tabs as MUITabs, Tab as MUITab } from '@mui/material';
import css from './Tabs.module.scss';
import { KeyboardDispatchTypes, useKeyboardDispatch } from '../../context/Keyboard/keyboard';
import { ActionMap } from '../../API/GoMoto.types';

const TabsContext = React.createContext<ITabsState | undefined>(undefined);
const TabsDispatchContext = React.createContext<Dispatch<TabActions>>(() => null);

interface ITabsState {
    currentTabIndex: number;
}

function useTabsContext() {
    const context = React.useContext(TabsContext);
    if (context === undefined) {
        throw new Error('TabsContext must be used within a, TabsContext Provider');
    }

    return context;
}

export const useTabsDispatchContext = () => {
    const context = React.useContext(TabsDispatchContext);
    if (context === undefined) {
        throw new Error('TabsDispatchContext must be used within a, TabsContext Provider');
    }

    return context;
};

interface ITabs {
    initialTabIndex: number;
    children: (false | undefined | ReactElement<ITabProps>)[];
    noBottomMargin?: boolean;
}

export enum TabTypes {
    Set = 'SET'
}

type TabPayload = {
    [TabTypes.Set]: number;
};

type TabActions = ActionMap<TabPayload>[keyof ActionMap<TabPayload>];

export const tabReducer = (state: ITabsState, action: TabActions) => {
    switch (action.type) {
        case TabTypes.Set:
            return { currentTabIndex: action.payload };
        default:
            return state;
    }
};

export const Tabs = React.memo((props: ITabs) => {
    const [state, dispatch] = React.useReducer(tabReducer, { currentTabIndex: props.initialTabIndex });
    const keyboardDispatch = useKeyboardDispatch();

    useEffect(() => {
        const foundTab = props.children.find((val) => (val as ReactElement<ITabProps>)?.props?.tabIndex === state.currentTabIndex) as ReactElement<ITabProps>;
        if (foundTab) {
            const hideKeyboard = !!foundTab.props.hideKeyboard;
            keyboardDispatch({ type: KeyboardDispatchTypes.KeyboardHidden,
                payload: hideKeyboard });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.currentTabIndex]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        dispatch({ type: TabTypes.Set,
            payload: newValue });
    };

    return (
        <TabsContext.Provider value={state}>
            <TabsDispatchContext.Provider value={dispatch}>
                <MUITabs
                    classes={{ root: `${props.noBottomMargin && css.noBottomMargin} ${css.tabsBorder} ${css.tabs}`,
                        indicator: css.indicator }}
                    value={state.currentTabIndex}
                    onChange={handleChange}
                    indicatorColor={'primary'}
                    textColor={'primary'}
                    variant={'fullWidth'}
                >
                    {React.Children.toArray(props.children)
                        .map((child, index) => (
                            <MUITab
                                classes={{ root: css.tab,
                                    selected: css.tabSelected }}
                                key={index}
                                title={(child as ReactElement<ITabProps>)?.props.title}
                                label={(child as ReactElement<ITabProps>)?.props.label}
                                onClick={(child as ReactElement<ITabProps>)?.props.onClick}
                            />
                        ))}
                </MUITabs>
                {React.Children.toArray(props.children)}
            </TabsDispatchContext.Provider>
        </TabsContext.Provider>
    );
});

export const TabFocusRefContext = React.createContext<React.MutableRefObject<any> | undefined>(undefined);

export function useTabFocusRefContext() {
    const context = React.useContext(TabFocusRefContext);
    if (context === undefined) {
        throw new Error('TabFocusRefContext must be used within a, TabFocusRefContext Provider');
    }

    return context;
}

interface ITabProps {
    tabIndex: number;
    label: string;
    title?: string;
    /**
     * The onClick function to trigger functionality when the tab is clicked on.
     */
    onClick?: MouseEventHandler<HTMLDivElement>;
    hideKeyboard?: boolean;
    addContainer?: boolean;
    /**
     * Tabs are only mounted after becoming active. They remain mounted afterwards
     */
    lazy?: boolean;
}

export const Tab: React.FC<PropsWithChildren<ITabProps>> = React.memo((props) => {
    const tabsContext = useTabsContext();
    const [visited, setVisited] = useState(false);

    const ref: React.MutableRefObject<any> = useRef();

    React.useEffect(() => {
        if (tabsContext.currentTabIndex === props.tabIndex) {
            ref.current?.focus();
            setVisited(true);
        }
    }, [props.tabIndex, tabsContext.currentTabIndex]);

    return (
        <TabFocusRefContext.Provider value={ref}>
            <div
                className={props.addContainer ? css.tabContainer : ''}
                role={'tabpanel'}
                hidden={tabsContext.currentTabIndex !== props.tabIndex}
            >
                {props.tabIndex === tabsContext.currentTabIndex || visited ? props.children : null}
            </div>
        </TabFocusRefContext.Provider>
    );
});
